import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { useListingDisplay } from '../../../../hooks/listing'

import { Text, If, Unless } from '../../../../components'
import { BreakEvenBarChart } from '../../../../components/Charts'

import styles from './SavingsGraph.module.scss'

const SavingsGraph = ({ className, compact, ...restProps }) => {
  const { savingsValues, breakEvenYears, yearsInHome } = useListingDisplay()

  const wrapperRef = useRef(null)
  const labelRef = useRef(null)
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 })
  const [labelStyle, setLabelStyle] = useState({ opacity: '0' })

  useEffect(() => {
    const wrapper = wrapperRef?.current
    if (wrapper && wrapper.offsetHeight && !dimensions.width) {
      const rect = wrapper.getBoundingClientRect()
      if (rect.height && rect.width) {
        setDimensions({ width: rect.width, height: rect.height })
      }
    }
  })

  useEffect(() => {
    const wrapper = wrapperRef?.current
    const label = labelRef?.current
    if (wrapper && label) {
      const wrapperRect = wrapper.getBoundingClientRect()
      const labelRect = label.getBoundingClientRect()
      const maxLeft = wrapperRect.width - labelRect.width
      setLabelStyle({
        opacity: '1',
        left: `${(Math.min(breakEvenYears, 20) / 20) * maxLeft + 4}px`,
      })
    }
  }, [breakEvenYears])

  if (!savingsValues || !savingsValues.length) {
    return <></>
  }

  const data = savingsValues.map((v, i) => ({ x: i + 1, y: v })).slice(0, 20)
  const negativeYears = breakEvenYears - 1

  return (
    <div
      ref={wrapperRef}
      className={classNames(className, styles.defaultChart)}
      {...restProps}
    >
      <BreakEvenBarChart
        {...dimensions}
        data={data}
        compact={compact}
        withEllipsis
        outsideScopeIndex={yearsInHome}
        breakEvenIndex={Math.max(0, breakEvenYears - 1)}
      />
      {!compact && (
        <div
          ref={labelRef}
          className={styles.breakEvenLabel}
          style={labelStyle}
        >
          <If condition={negativeYears >= 0}>
            <Text weight='bold' color='heather' size='s' element='span'>
              {`${negativeYears} year${negativeYears !== 1 ? 's ' : ' '}`}
            </Text>
            <Text color='heather' size='s' element='span'>
              to break even
              <sup>
                <i>*</i>
              </sup>
            </Text>
          </If>
          <Unless condition={negativeYears >= 0}>
            <Text color='heather' size='s' element='span'>
              no break even
              <sup>
                <i>*</i>
              </sup>
            </Text>
          </Unless>
        </div>
      )}
    </div>
  )
}

SavingsGraph.propTypes = {
  compact: PropTypes.bool,
  className: PropTypes.string,
}

export default SavingsGraph
